
import SVGCircleFactory from './svg-lib/factories/svg-circle-factory';
import SVGPathFactory from './svg-lib/factories/svg-path-factory';
import SVGTextareaFactory from './svg-lib/factories/svg-textarea-factory';
import SVGComicFactory from './svg-lib/factories/svg-comic-factory';

const SVG_NS = 'http://www.w3.org/2000/svg';

const pickLastColorClass = (classList, allowed) => {
  for (let i = classList.length - 1; i >= 0; i -= 1) {
    if (allowed.includes(classList[i])) return classList[i];
  }
  return null;
};


export default class SVGSave {
  static _isReadOnlyMode() {
    return HISTORYLAB.import && HISTORYLAB.import.done === true;
  }

  static _applyReadOnlyState(textarea) {
    if (!textarea) return;

    if (SVGSave._isReadOnlyMode()) {
      textarea.readOnly = true;
      textarea.setAttribute('readonly', 'readonly');
    }
  }

  static _resizeComicTextarea(textarea) {
    if (!textarea) return;

    textarea.style.height = '1px';
    const newHeight = `${22 + textarea.scrollHeight}px`;
    textarea.style.height = newHeight;

    const $parent = textarea.parentElement;
    if (!$parent) return;

    $parent.style.height = newHeight;
  }

  // Read user-created SVG content into a compact JSON structure
  static getData(target) {
    const $canvas = target.querySelector('.svg-view') || target;
    const viewBox = target.getAttribute('viewBox') || '';

    return {
      viewBox,
      paths: SVGSave._extractPaths($canvas),
      circles: SVGSave._extractCircles($canvas),
      draggings: SVGSave._extractDraggings($canvas),
      textareas: SVGSave._extractTextareas($canvas),
      comics: SVGSave._extractComics($canvas),
    };
  }

  //DONE
  static _extractPaths(root) {
    const COLORS = ['blue', 'red', 'yellow'];
    return Array.from(root.querySelectorAll('path[data-svg-path]')).map(
      (el) => ({
        id: el.id || null,
        d: (el.getAttribute('d') || '').trim(),
        color: pickLastColorClass(el.classList, COLORS),
        clone: el.hasAttribute('data-svg-clone'),
      }),
    );
  }

  // DONE
  static _extractCircles(root) {
    const COLORS = ['blue', 'red', 'orange', 'yellow'];
    return Array.from(
      root.querySelectorAll('circle[data-svg-type="circle"][data-svg-circle]'),
    )
      .filter((el) => !el.hasAttribute('data-svg-comic'))
      .map((el) => {
      const resAttr = el.getAttribute('data-svg-resolution') || '';
      const [w = '', h = ''] = resAttr.split(',');
      return {
        id: el.id || null,
        position: {
          x: Number(el.getAttribute('cx')),
          y: Number(el.getAttribute('cy')),
        },
        r: Number(el.getAttribute('r')) || 8,
        dataSvgResolution: {
          width: Number(w.trim()),
          height: Number(h.trim()),
        },
        color: pickLastColorClass(el.classList, COLORS),
        clone: el.hasAttribute('data-svg-clone'),
      };
    });
  }

  static _extractDraggings(root) {
    return Array.from(
      root.querySelectorAll('foreignObject[data-svg-type="pretahovani"]'),
    ).map((el) => {
      const resAttr = el.getAttribute('data-svg-resolution') || '';
      const [w = '', h = ''] = resAttr.split(',');
      const width = Number(el.getAttribute('width'));
      const height = Number(el.getAttribute('height'));
      const x = Number(el.getAttribute('x'));
      const y = Number(el.getAttribute('y'));
      const itemDiv = el.querySelector('.dropped__item');

      // Extract medium type
      let medium = 'default';
      if (itemDiv) {
        const typeClass = Array.from(itemDiv.classList).find((c) =>
          c.startsWith('dropped__item--'),
        );
        if (typeClass) {
          medium = typeClass.replace('dropped__item--', '');
        }
      }

      // Extract source data-id reference
      const sourceId = el.getAttribute('data-source-id') || null;

      // Extract content based on medium type
      let content = null;
      if (itemDiv) {
        switch (medium) {
          case 'tag': {
            const span = itemDiv.querySelector('.keywords-item span');
            content = span ? span.textContent.trim() : null;
            break;
          }
          case 'image': {
            const img = itemDiv.querySelector('img');
            content = img ? img.getAttribute('src').replace('../assets/img/', '') : null;
            break;
          }
          case 'user-text': {
            const textarea = itemDiv.querySelector('textarea');
            content = textarea ? textarea.value : null;
            break;
          }
          case 'svg': {
            // First try to get from foreignObject data attributes
            const svgId = el.getAttribute('data-svg-id');
            const svgImage = el.getAttribute('data-svg-image');

            if (svgId && svgImage) {
              content = { id: svgId, soubor: svgImage };
            } else {
              // Fallback: extract from rendered SVG element
              const svg = itemDiv.querySelector('svg');
              if (svg) {
                const svgIdFromElement = svg.id.replace('-svg', '');
                const image = svg.querySelector('image');
                const href = image ? image.getAttribute('xlink:href') : null;
                content = {
                  id: svgIdFromElement,
                  soubor: href ? href.replace('../assets/img/', '') : null
                };
              } else {
                content = null;
              }
            }
            break;
          }
          case 'audio': {
            const audio = itemDiv.querySelector('audio source');
            content = audio ? audio.getAttribute('src').replace('../assets/audio/', '') : null;
            break;
          }
          case 'video': {
            const video = itemDiv.querySelector('video source');
            content = video ? video.getAttribute('src').replace('../assets/video/', '') : null;
            break;
          }
          case 'text': {
            const span = itemDiv.querySelector('.text__item span');
            content = span ? span.innerHTML : null;
            break;
          }
          default:
            content = null;
        }
      }

      return {
        id: el.id || null,
        sourceId,
        medium,
        content,
        elementResolution: { width, height },
        position: { x, y },
        dataSvgResolution: {
          width: Number(w.trim()),
          height: Number(h.trim()),
        },
        clone: el.hasAttribute('data-svg-clone'),
      };
    });
  }

  static _extractTextareas(root) {
    return Array.from(
      root.querySelectorAll('foreignObject.svg-text[data-svg-text]'),
    )
      .filter((el) => {
        // Exclude textareas inside g.svg-komiks
        if (el.closest('g.svg-komiks')) return false;
        // Exclude cloned comic bubbles
        const textarea = el.querySelector('textarea');
        if (textarea && textarea.hasAttribute('data-cloned-from-comic')) return false;
        return true;
      })
      .map((el) => {
        const textarea = el.querySelector('textarea');
        const resAttr = el.getAttribute('data-svg-resolution') || '';
        const [w = '', h = ''] = resAttr.split(',');
        const orientationAttr =
          el.getAttribute('data-svg-text-orientation') || '';
        const foStyle = el.getAttribute('style') || '';

        // Extract width and height from style
        const widthMatch = foStyle.match(/width:\s*([^;]+)/);
        const heightMatch = foStyle.match(/height:\s*([^;]+)/);
        const foWidth = widthMatch ? widthMatch[1].trim() : '15.2rem';
        const foHeight = heightMatch ? heightMatch[1].trim() : '55px';

        const dataSvgTextOrientation = orientationAttr
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        return {
          id: textarea ? textarea.id : null,
          position: {
            x: Number(el.getAttribute('x')),
            y: Number(el.getAttribute('y')),
          },
          dataSvgTextOrientation: dataSvgTextOrientation.length
            ? dataSvgTextOrientation
            : null,
          dataSvgResolution: {
            width: Number(w.trim()) || null,
            height: Number(h.trim()) || null,
          },
          foreignObjectResolution: { width: foWidth, height: foHeight },
          value: textarea ? textarea.value : null,
          clone: textarea ? textarea.hasAttribute('data-svg-clone') : false,
        };
      });
  }

  // Extracts both comic textareas and comic circles
  static _extractComics(root) {
    const COLORS = ['blue', 'red', 'orange', 'yellow'];

    // Extract comic textareas from g.svg-komiks (PUG-generated)
    const textareas = Array.from(
      root.querySelectorAll('foreignObject.svg-komiks-text[data-svg-text]'),
    )
      .filter((el) => !!el.closest('g.svg-komiks'))
      .map((el) => {
        const textarea = el.querySelector('textarea');
        const label = el.querySelector('.svg-category-label');
        const subjectAttr = el.getAttribute('data-svg-subject') || '';
        const subject = subjectAttr ? subjectAttr.split(',').map(s => s.trim()).filter(Boolean) : null;
        return {
          type: 'textarea',
          id: textarea ? textarea.id : null,
          position: {
            x: Number(el.getAttribute('x')),
            y: Number(el.getAttribute('y')),
          },
          dataSvgCategory: textarea
            ? textarea.getAttribute('data-svg-category') || null
            : null,
          subject: subject,
          labelText: label ? label.textContent : null,
          value: textarea ? textarea.value : null,
          clone: textarea ? textarea.hasAttribute('data-svg-clone') : false,
          isClonedComic: false,
        };
      });

    // Extract cloned comic bubbles (created by _cloneComicBubbles)
    const clonedComics = Array.from(
      root.querySelectorAll('foreignObject.svg-text[data-svg-text] textarea[data-cloned-from-comic="true"]'),
    ).map((textarea) => {
      const fo = textarea.parentElement;
      const label = fo.querySelector('.svg-category-label');
      const subjectAttr = fo.getAttribute('data-svg-subject') || '';
      const subject = subjectAttr ? subjectAttr.split(',').map(s => s.trim()).filter(Boolean) : null;
      return {
        type: 'textarea',
        id: textarea.id || null,
        position: {
          x: Number(fo.getAttribute('x')),
          y: Number(fo.getAttribute('y')),
        },
        dataSvgCategory: textarea.getAttribute('data-svg-category') || null,
        subject: subject,
        labelText: label ? label.textContent : null,
        value: textarea.value || null,
        clone: textarea.hasAttribute('data-svg-clone'),
        isClonedComic: true,
      };
    });

    // Extract comic circles
    const circles = Array.from(
      root.querySelectorAll('circle[data-svg-comic="true"]'),
    ).map((el) => {
      const resAttr = el.getAttribute('data-svg-resolution') || '';
      const [w = '', h = ''] = resAttr.split(',');
      return {
        type: 'circle',
        id: el.id || null,
        position: {
          x: Number(el.getAttribute('cx')),
          y: Number(el.getAttribute('cy')),
        },
        r: Number(el.getAttribute('r')) || 8,
        dataSvgResolution: {
          width: Number(w.trim()),
          height: Number(h.trim()),
        },
        color: pickLastColorClass(el.classList, COLORS),
        clone: el.hasAttribute('data-svg-clone'),
      };
    });

    return [...textareas, ...clonedComics, ...circles];
  }

  // Render stored data back into SVG
  static loadData(target, data) {
    const $svgInstance = target.querySelector('.svg-view') || target;

    SVGSave._clearUserContent($svgInstance);

    SVGSave._renderPaths($svgInstance, data.paths || []);
    SVGSave._renderCircles($svgInstance, data.circles || []);
    SVGSave._renderDraggings($svgInstance, data.draggings || []);
    SVGSave._renderTextareas($svgInstance, data.textareas || [], target.id);
    SVGSave._renderComics($svgInstance, data.comics || []);
  }

  static _clearUserContent(root) {
    // Remove all user-created content except elements inside g.svg-komiks
    root.querySelectorAll('path[data-svg-path]').forEach((n) => {
      if (!n.closest('g.svg-komiks')) n.remove();
    });
    root.querySelectorAll('circle[data-svg-type="circle"][data-svg-circle]').forEach((n) => {
      if (!n.closest('g.svg-komiks')) n.remove();
    });
    root.querySelectorAll('foreignObject[data-svg-type="pretahovani"]').forEach((n) => {
      if (!n.closest('g.svg-komiks')) n.remove();
    });
    root.querySelectorAll('foreignObject.svg-text[data-svg-text]').forEach((n) => {
      if (!n.closest('g.svg-komiks')) n.remove();
    });
  }

  // Done
  static _renderPaths($svgInstance, paths) {

    paths.forEach(({ id, d, color, clone }) => {
      // Použij factory pro vytvoření elementu
      const path = SVGPathFactory.create({
        id,
        d,
        color,
        clone,
      });

      $svgInstance.appendChild(path);
    });
  }

  //DONE
  static _renderCircles($svgInstance, circles) {

    circles.forEach(({ id, position: { x, y }, r, dataSvgResolution, resolution, color, clone }) => {
      // Použij factory pro vytvoření elementu
      const circle = SVGCircleFactory.create({
        id,
        cx: x,
        cy: y,
        r: r || 8,
        color,
        dataSvgResolution: dataSvgResolution || resolution, // backward compatibility
        clone,
      });

      $svgInstance.appendChild(circle);
    });
  }

  // Renders dragging elements back to SVG
  // Note: This creates the foreignObject structure, but actual content rendering
  // will be handled by drag-and-drop system when it finds matching sourceId
  static _renderDraggings(root, draggings) {
    draggings.forEach(({
      id,
      sourceId,
      medium,
      content,
      elementResolution,
      position,
      dataSvgResolution,
    }) => {
      const fo = document.createElementNS(SVG_NS, 'foreignObject');
      fo.setAttribute('data-svg-type', 'pretahovani');
      fo.setAttribute('width', elementResolution.width || 0);
      fo.setAttribute('height', elementResolution.height || 0);
      fo.setAttribute('x', position.x || 0);
      fo.setAttribute('y', position.y || 0);

      if (dataSvgResolution) {
        fo.setAttribute(
          'data-svg-resolution',
          `${dataSvgResolution.width || 0}, ${dataSvgResolution.height || 0}`,
        );
      }

      if (sourceId) {
        fo.setAttribute('data-source-id', sourceId);
      }

      if (id) fo.id = id;

      // Create basic dropped__item container
      const div = document.createElement('div');
      div.className = `dropped__item dropped__item--${medium} unselectable`;
      div.style.width = `${elementResolution.width || 0}px`;
      div.style.height = `${elementResolution.height || 0}px`;

      // Render content based on medium type
      if (content) {
        switch (medium) {
          case 'tag': {
            div.innerHTML = `<div class="keywords-item unselectable" style="width: ${elementResolution.width}px; height: ${elementResolution.height}px; padding-left: 15px;"><span style="padding-left: 7px;">${content}</span><div class="keywords-item-icon"></div></div>`;
            break;
          }
          case 'image': {
            div.innerHTML = `<img src="../assets/img/${content}" alt="">`;
            break;
          }
          case 'user-text': {
            div.innerHTML = `<textarea>${content}</textarea>`;
            break;
          }
          case 'svg': {
            if (typeof content === 'object' && content !== null) {
              // Store as separate attributes for reconstruction
              fo.setAttribute('data-svg-id', content.id || '');
              fo.setAttribute('data-svg-image', content.soubor || '');
              // Mark that this needs SVG rendering
              div.setAttribute('data-needs-svg-render', 'true');
            } else {
              // Backward compatibility: content is just ID string
              fo.setAttribute('data-svg-id', content || '');
              div.setAttribute('data-needs-svg-render', 'true');
            }
            break;
          }
          case 'audio': {
            div.innerHTML = `<audio><source src="../assets/audio/${content}"></audio>`;
            break;
          }
          case 'video': {
            div.innerHTML = `<video><source src="../assets/video/${content}"></video>`;
            break;
          }
          case 'text': {
            div.innerHTML = `<div class="text__item"><span>${content}</span></div>`;
            break;
          }
          default:
            console.warn(`Unknown medium type: ${medium}`);
        }
      }

      fo.appendChild(div);
      root.appendChild(fo);
    });
  }

  static _renderTextareas(root, textareas, svgID) {

    textareas.forEach(({
      id,
      position,
      dataSvgTextOrientation,
      dataSvgResolution,
      foreignObjectResolution,
      value,
      clone,
    }) => {
      // Použij factory pro vytvoření struktury
      const { group, textarea } = SVGTextareaFactory.create({
        id,
        position,
        dataSvgResolution,
        dataSvgTextOrientation,
        foWidth: foreignObjectResolution?.width || '15.2rem',
        foHeight: foreignObjectResolution?.height || '55px',
        value,
        clone,
      });

      // Přidej atribut data-svg-textarea-target
      if (svgID) {
        textarea.setAttribute('data-svg-textarea-target', svgID);
      }

      // Apply read-only state if in done mode
      SVGSave._applyReadOnlyState(textarea);

      // Add hover-to-front listener for read-only mode
      if (SVGSave._isReadOnlyMode()) {
        textarea.addEventListener('mouseover', () => {
          if (group && group.classList.contains('svg-textarea-group')) {
            d3.select(group).raise();
          }
        });
      }

      root.appendChild(group);
    });
  }

  // Updates comic textareas values (both circles and textareas are already in PUG)
  static _renderComics(root, comics) {
    comics.forEach((item) => {
      // Skip circles - they're already rendered from PUG template
      if (item.type === 'circle') {
        return;
      }

      if (item.type === 'textarea') {
        const { id, value, isClonedComic } = item;

        // Cloned comics need to be created as foreignObject structure
        if (isClonedComic) {
          const { position, dataSvgCategory, subject, labelText, clone } = item;

          const fo = SVGComicFactory.create({
            id,
            position,
            dataSvgCategory,
            subject,
            labelText,
            value,
            clone,
            isClonedComic: true,
            isPugGenerated: false,
          });

          root.appendChild(fo);
          const textarea = fo.querySelector('textarea');
          SVGSave._applyReadOnlyState(textarea);
          SVGSave._resizeComicTextarea(textarea);
        } else {
          // PUG-generated comics - just update value
          if (id) {
            const existingTextarea = root.querySelector(`textarea#${id}`);
            if (existingTextarea) {
              // Update value of existing PUG-generated textarea
              existingTextarea.value = value || '';
              SVGSave._applyReadOnlyState(existingTextarea);
              SVGSave._resizeComicTextarea(existingTextarea);
            } else {
              // Fallback: if textarea doesn't exist, create it (for backward compatibility)
              console.warn(`Comic textarea with id "${id}" not found in PUG, creating new one`);
              const { position, dataSvgCategory, subject, labelText } = item;

              const fo = SVGComicFactory.create({
                id,
                position,
                dataSvgCategory,
                subject,
                labelText,
                value,
                clone: false,
                isClonedComic: false,
                isPugGenerated: true,
              });

              root.appendChild(fo);
              const textarea = fo.querySelector('textarea');
              SVGSave._applyReadOnlyState(textarea);
              SVGSave._resizeComicTextarea(textarea);
            }
          }
        }
      }
    });
  }
}
